import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import * as LanternApi from '../utilities/LanternApiClient';

const RetailerCacheContext = createContext();

/**
 * Hook to access cached retailer data
 */
export const useRetailerCache = () => {
  const context = useContext(RetailerCacheContext);
  if (!context) {
    throw new Error('useRetailerCache must be used within a RetailerCacheProvider');
  }
  return context;
};

/**
 * Provider component to cache retailer data and prevent duplicate fetches
 */
export const RetailerCacheProvider = ({ children }) => {
  const [cache, setCache] = useState({
    allRetailers: null,
    byParentClient: {},
  });
  const [loadingStates, setLoadingStates] = useState({
    allRetailers: false,
    byParentClient: {},
  });

  // Track ongoing requests to prevent duplicates
  const requestsRef = useRef({
    allRetailers: null,
    byParentClient: {},
  });

  /**
   * Fetch all retailers with caching
   */
  const getAllRetailers = useCallback(
    async (forceRefresh = false) => {
      // Return cached data if available and not forcing refresh
      if (cache.allRetailers && !forceRefresh) {
        return cache.allRetailers;
      }

      // Return existing request if already in progress
      if (requestsRef.current.allRetailers) {
        return requestsRef.current.allRetailers;
      }

      setLoadingStates((prev) => ({ ...prev, allRetailers: true }));

      try {
        const requestPromise = LanternApi.getRetailers();
        requestsRef.current.allRetailers = requestPromise;

        const response = await requestPromise;

        if (response.success) {
          const retailers = response.body.records;
          setCache((prev) => ({ ...prev, allRetailers: retailers }));
          return retailers;
        } else {
          console.warn('Failed to fetch all retailers');
          return [];
        }
      } catch (error) {
        console.error('Error fetching all retailers:', error);
        return [];
      } finally {
        setLoadingStates((prev) => ({ ...prev, allRetailers: false }));
        requestsRef.current.allRetailers = null;
      }
    },
    [cache.allRetailers],
  );

  /**
   * Fetch retailers by parent client with caching
   */
  const getRetailersByParentClient = useCallback(
    async (parentClientId, forceRefresh = false) => {
      if (!parentClientId) {
        return getAllRetailers(forceRefresh);
      }

      const cacheKey = parentClientId.toString();

      // Return cached data if available and not forcing refresh
      if (cache.byParentClient[cacheKey] && !forceRefresh) {
        return cache.byParentClient[cacheKey];
      }

      // Return existing request if already in progress
      if (requestsRef.current.byParentClient[cacheKey]) {
        return requestsRef.current.byParentClient[cacheKey];
      }

      setLoadingStates((prev) => ({
        ...prev,
        byParentClient: { ...prev.byParentClient, [cacheKey]: true },
      }));

      try {
        const requestPromise = LanternApi.getRetailersByParentClient(parentClientId);
        requestsRef.current.byParentClient[cacheKey] = requestPromise;

        const response = await requestPromise;

        if (response.success) {
          const retailers = response.body;
          setCache((prev) => ({
            ...prev,
            byParentClient: { ...prev.byParentClient, [cacheKey]: retailers },
          }));
          return retailers;
        } else {
          console.warn('Failed to fetch retailers by parent client:', parentClientId);
          return [];
        }
      } catch (error) {
        console.error('Error fetching retailers by parent client:', error);
        return [];
      } finally {
        setLoadingStates((prev) => ({
          ...prev,
          byParentClient: { ...prev.byParentClient, [cacheKey]: false },
        }));
        if (requestsRef.current.byParentClient[cacheKey]) {
          delete requestsRef.current.byParentClient[cacheKey];
        }
      }
    },
    [cache.byParentClient, getAllRetailers],
  );

  /**
   * Get the appropriate retailers based on whether a parent client is selected
   * This method encapsulates the logic of choosing between all retailers vs parent-specific
   */
  const getRetailers = useCallback(
    async (parentClientId = null, forceRefresh = false) => {
      if (!parentClientId) {
        return getAllRetailers(forceRefresh);
      } else {
        return getRetailersByParentClient(parentClientId, forceRefresh);
      }
    },
    [getAllRetailers, getRetailersByParentClient],
  );

  /**
   * Filter retailers to only those with content specifications
   */
  const getRetailersWithContentSpecifications = useCallback((retailers) => {
    return retailers.filter((retailer) => retailer.contentSpecification);
  }, []);

  /**
   * Get loading state
   */
  const isLoading = useCallback(
    (type, parentClientId = null) => {
      if (type === 'allRetailers') {
        return loadingStates.allRetailers;
      } else if (type === 'byParentClient') {
        const cacheKey = parentClientId?.toString() || '';
        return loadingStates.byParentClient[cacheKey] || false;
      }
      return false;
    },
    [loadingStates],
  );

  /**
   * Clear cache
   */
  const clearCache = useCallback(() => {
    setCache({ allRetailers: null, byParentClient: {} });
    setLoadingStates({ allRetailers: false, byParentClient: {} });
    requestsRef.current = { allRetailers: null, byParentClient: {} };
  }, []);

  const contextValue = {
    // Methods
    getAllRetailers,
    getRetailersByParentClient,
    getRetailers,
    getRetailersWithContentSpecifications,
    clearCache,
    isLoading,

    // Data
    cache,
  };

  return <RetailerCacheContext.Provider value={contextValue}>{children}</RetailerCacheContext.Provider>;
};

export default RetailerCacheContext;
